"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "@store/features/authSlice";
import { updateRememberMe } from "@store/features/userSlice";
import { selectUserSettings } from "@store/selectors/user.selectors";
import { InputIcon, Input, Label, Checkbox } from "keep-react";
import { Envelope, Lock, Eye, EyeSlash } from "phosphor-react";
import { useStyleSchema } from '@components/styleSchema/StyleSchemaHandler';
import { AppDispatch } from "@store/store";
import { useCookieConsent } from "@hooks/useCookieConsent";

import { useLoginMutation } from "@store/services/ninoxAuthService";
import { useRouter } from "next/navigation";
import { ROUTES } from "@constants/routes";
import { closeLoginModal, openPasswordRecoveryModal } from "@store/features/userActionsSlice";

// Schema für die Formularvalidierung
const loginSchema = z.object({
  mail: z.string().email({
    message: "Bitte gib eine gültige E-Mail-Adresse ein"
  }),
  password: z.string().min(4, {
    message: "Das Passwort muss mindestens 4 Zeichen lang sein"
  }),
});

// Typ für die Formulardaten aus dem Schema ableiten
type LoginFormData = z.infer<typeof loginSchema>;

// Default-Werte für Entwicklungsumgebung
const defaultValues =
  process.env.NODE_ENV === "development"
    ? {
        mail: process.env.NEXT_PUBLIC_TEST_MAIL,
        password: process.env.NEXT_PUBLIC_TEST_PASSWORD,
      }
    : { mail: "", password: "" };

export function LoginForm() {
  const { t } = useTranslation();
  const [login] = useLoginMutation();
  const dispatch = useDispatch<AppDispatch>();
  const userSettings = useSelector(selectUserSettings);
  const router = useRouter();
  const { currentSchema } = useStyleSchema();
  const buttonColor = currentSchema.colors?.primary?.hex;
  const { cookiesAccepted } = useCookieConsent();

  // State für serverseitige Fehler und Passwort-Sichtbarkeit
  const [serverError, setServerError] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(userSettings.rememberMe);

  // React Hook Form mit Zod-Validierung
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid }
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues,
    mode: "onChange" // Validierung bei jeder Änderung ausführen
  });

  // Passwort-Recovery-Modal öffnen
  const handleOpenPasswordRecovery = () => {
    dispatch(openPasswordRecoveryModal());
    dispatch(closeLoginModal());
  };

  // Formular absenden
  const onSubmit = async (data: LoginFormData) => {
    try {
      setServerError(""); // Zurücksetzen von Serverfehlern
      const result = await login(data).unwrap();
      
      if (typeof result === "string") {
        setServerError(result);
        return;
      }

      // Anmeldedaten speichern
      dispatch(setCredentials({ 
        ...result, 
        rememberMe,
        cookiesAccepted 
      }));
      localStorage.setItem("auth_token", result.jwt);
      
      // Login-Modal schließen
      dispatch(closeLoginModal());
      
      // Zur My Account Seite navigieren
      router.push(ROUTES.myAccount);
    } catch (err) {
      console.error('Login error:', err);
      setServerError(t("auth-errors.login-failed"));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        {/* E-Mail-Feld */}
        <fieldset>
          <div className="space-y-2">
            <Label
              htmlFor="mail"
              className="text-[var(--color-slate-600)] font-inter font-medium leading-5 tracking-tight"
            >
              {t("auth.email")}
            </Label>
            <div className="relative">
              <Input
                id="mail"
                type="email"
                {...register("mail")}
                className={
                  errors.mail
                    ? "border-[var(--color-red-500)] text-[var(--color-red-500)] ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:border-1"
                    : "ps-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--color-slate-600)] focus-visible:ring-0 focus-visible:border-1"
                }
              />
              <InputIcon>
                <Envelope
                  size={19}
                  className={
                    errors.mail
                      ? "text-[var(--color-red-500)]"
                      : "text-[#455468]"
                  }
                />
              </InputIcon>
            </div>
            {errors.mail && (
              <p className="text-red-500">
                {t("auth-errors.invalid-email")}
              </p>
            )}
          </div>
        </fieldset>

        {/* Passwort-Feld */}
        <fieldset>
          <div className="space-y-2 relative">
            <Label
              htmlFor="password"
              className="text-[var(--color-slate-600)] font-inter font-medium leading-5 tracking-tight"
            >
              {t("auth.password")}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={isVisible ? "text" : "password"}
                {...register("password")}
                className={
                  errors.password
                    ? "border-[var(--color-red-500)] text-[var(--color-red-500)] ps-11 pe-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:border-1"
                    : "ps-11 pe-11 focus:outline-none focus:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--color-slate-600)] focus-visible:ring-0 focus-visible:border-1"
                }
              />
              <InputIcon>
                <Lock
                  size={19}
                  className={
                    errors.password
                      ? "text-[var(--color-red-500)]"
                      : "text-[#455468]"
                  }
                />
              </InputIcon>
              <InputIcon
                onClick={() => setIsVisible(!isVisible)}
                className="absolute pointer-events-auto inset-y-0 start-auto end-3 flex items-center cursor-pointer"
              >
                {isVisible ? (
                  <EyeSlash size={19} color="#455468" />
                ) : (
                  <Eye size={19} color="#455468" />
                )}
              </InputIcon>
            </div>
            {errors.password && (
              <p className="text-red-500">
                {t("auth-errors.invalid-password")}
              </p>
            )}
          </div>
        </fieldset>
      </div>

      {/* Serverfehler anzeigen */}
      {serverError && <p className="text-red-500">{serverError}</p>}

      {/* Remember Me und Passwort vergessen */}
      <div className="flex justify-between">
        <fieldset className="flex items-center gap-3">
          <Checkbox
            className="w-4 h-4 border-[#AFBACA] border bg-[var(--color-slate-25)] appearance-none data-[state=checked]:bg-[var(--color-blue-action-600)] data-[state=checked]:text-primary-metal-600 data-[state=checked]:border-[#AFBACA] data-[state=checked]:text-[#455468]"
            id="rememberMe"
            checked={rememberMe}
            onCheckedChange={(checked: boolean | "indeterminate") => {
              const isChecked = checked === true;
              setRememberMe(isChecked);
              dispatch(updateRememberMe(isChecked));
            }}
          />
          <Label
            htmlFor="rememberMe"
            className="font-normal text-left text-[var(--color-zinc-800)]"
          >
            {t("auth.remember-data")}
          </Label>
        </fieldset>
        <button
          type="button"
          onClick={handleOpenPasswordRecovery}
          className="text-[var(--accent-color)] hover:underline font-medium text-xs"
        >
          {t("auth.forgot-password")}
        </button>
      </div>

      {/* Submit-Button */}
      <div className="w-full flex justify-center items-center">
        <button
          type="submit"
          className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: buttonColor,
            color: "white",
          }}
          disabled={isSubmitting || !isValid}
        >
          {t("auth.login")}
        </button>
      </div>
    </form>
  );
}